import { useCallback, useEffect, useState } from 'react';
import {
  approveRefund,
  completeRequisitesRefund,
  getRefundRequests,
  rejectRefund,
} from '../../api/orderBoard';
import styles from './returns.module.css';

const money = value => new Intl.NumberFormat('ru-RU', {
  style: 'currency', currency: 'RUB', maximumFractionDigits: 2,
}).format(value || 0);

const date = value => value ? new Date(value).toLocaleString('ru-RU', {
  day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '';

const statusLabels = {
  review: 'Ожидает проверки',
  approved: 'Одобрено',
  processing: 'Возврат выполняется',
  awaiting_requisites_transfer: 'Ожидает перевода по реквизитам',
  succeeded: 'Возврат выполнен',
  rejected: 'Отклонено',
  failed: 'Ошибка возврата',
};

export default function Returns({ jwt }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workingId, setWorkingId] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await getRefundRequests(jwt);
      if (!response.ok) throw new Error(`Ошибка загрузки: ${response.status}`);
      setItems(await response.json());
      setError('');
    } catch (e) {
      setError(e.message || 'Не удалось загрузить заявки.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [jwt]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = window.setInterval(() => load(true), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  const mutate = async (id, action) => {
    setWorkingId(id);
    try {
      const response = await action();
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `Ошибка: ${response.status}`);
      }
      await load(true);
    } catch (e) {
      window.alert(e.message || 'Не удалось изменить заявку.');
    } finally {
      setWorkingId(null);
    }
  };

  const approve = item => {
    if (!window.confirm(`Вы связались с клиентом и подтверждаете возврат по заказу №${item.id}?`)) return;
    mutate(item.id, () => approveRefund(item.id, jwt));
  };

  const reject = item => {
    const comment = window.prompt('Укажите причину отказа. Клиент увидит этот текст:');
    if (comment === null) return;
    if (comment.trim().length < 5) {
      window.alert('Причина должна содержать минимум 5 символов.');
      return;
    }
    mutate(item.id, () => rejectRefund(item.id, comment.trim(), jwt));
  };

  const completeTransfer = item => {
    if (!window.confirm('Подтвердите, что деньги уже переведены на указанные реквизиты. Это действие завершит возврат.')) return;
    mutate(item.id, () => completeRequisitesRefund(item.id, jwt));
  };

  if (loading) return <div className={styles.state}>Загружаем заявки…</div>;

  return (
    <section className={styles.page}>
      <div className={styles.heading}>
        <div>
          <h1>Возвраты</h1>
          <p>Сначала свяжитесь с клиентом, затем примите решение по заявке.</p>
        </div>
        <button className={styles.refresh} onClick={() => load()} type="button">Обновить</button>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {!items.length && !error && <div className={styles.empty}>Заявок на возврат пока нет.</div>}

      <div className={styles.list}>
        {items.map(item => {
          const bank = (item.bankAmountKopecks || 0) / 100;
          return (
            <article className={styles.card} key={item.id}>
              <div className={styles.cardTop}>
                <div>
                  <h2>Заказ №{item.id}</h2>
                  <span>{date(item.refundRequestedAt)}</span>
                </div>
                <span className={`${styles.status} ${styles[item.refundStatus] || ''}`}>
                  {statusLabels[item.refundStatus] || item.refundStatus}
                </span>
              </div>

              <div className={styles.contact}>
                <strong>Свяжитесь с клиентом до принятия решения</strong>
                <a href={`tel:${item.phone}`}>{item.receiver}, {item.phone}</a>
                {item.email && <a href={`mailto:${item.email}`}>{item.email}</a>}
              </div>

              <div className={styles.problem}>
                <span>Описание проблемы</span>
                <p>{item.refundDescription}</p>
              </div>

              {!!item.refundPhotoUrls?.length && (
                <div className={styles.photos}>
                  {item.refundPhotoUrls.map(url => (
                    <a href={url} key={url} target="_blank" rel="noreferrer">
                      <img alt="Фото к заявке" src={url} />
                    </a>
                  ))}
                </div>
              )}

              <div className={styles.moneyGrid}>
                <div><span>Сумма заказа</span><strong>{money(item.price)}</strong></div>
                {item.externalAmount > 0 && <div><span>{item.customerType === 'company' ? 'По реквизитам' : 'На способ оплаты'}</span><strong>{money(item.externalAmount)}</strong></div>}
                {bank > 0 && <div><span>Баллы / внутренний баланс</span><strong>{money(bank)}</strong></div>}
              </div>

              {item.customerType === 'company' && (
                <div className={styles.requisites}>
                  <strong>{item.companyKind} «{item.companyName}»</strong>
                  <span>ИНН {item.companyInn}{item.companyKpp ? ` · КПП ${item.companyKpp}` : ''}</span>
                  <span>{item.companyBankName} · БИК {item.companyBik}</span>
                  <span>р/с {item.companyCheckingAccount}</span>
                  <span>к/с {item.companyCorrespondentAccount}</span>
                </div>
              )}

              {item.refundManagerComment && <div className={styles.comment}><strong>Комментарий менеджера:</strong> {item.refundManagerComment}</div>}

              <div className={styles.actions}>
                {item.refundStatus === 'review' && <>
                  <button disabled={workingId === item.id} className={styles.approve} onClick={() => approve(item)} type="button">Подтвердить возврат</button>
                  <button disabled={workingId === item.id} className={styles.reject} onClick={() => reject(item)} type="button">Отклонить</button>
                </>}
                {item.refundStatus === 'awaiting_requisites_transfer' && (
                  <button disabled={workingId === item.id} className={styles.approve} onClick={() => completeTransfer(item)} type="button">Деньги переведены по реквизитам</button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
